/**
 * C03 Monsters UI — three surfaces:
 *   1. `renderMonsterRevealBanner` — kid-facing home banner that fires once
 *      when `monsters.lastNew` is set; consumes the marker so it doesn't
 *      re-fire on subsequent home renders. Shows the caught monster's emoji
 *      + name + 「✨ 抓到新怪獸!」 copy.
 *   2. `openMonsterDexModal` — full-screen Pokédex grid (5 faction tabs ×
 *      caught + locked entries). Locked entries shown greyscale with ???
 *      name + shadow silhouette. Includes an EVOLVE button when the kid
 *      has enough duplicates.
 *   3. `showEvolveCelebration` — full-screen celebration overlay that runs
 *      when an evolution completes (caller fires via window event or
 *      directly after evolveMonster).
 *
 * Inline styles (matches dayone-banner / badges-ui / streak-ui pattern).
 * No external CSS. Pure presentation — all state mutation in monsters.ts.
 *
 * 🔥 學習與遊戲分離 (CLAUDE.md): DOM-only. Never touches src/game/.
 */

import type { PlayerProgress, FactionId } from '../../shared/types';
import { FACTIONS } from '../../shared/factions';
import {
  EVOLVE_COST,
  buildDexSnapshot,
  canEvolve,
  consumeLastNew,
  evolveMonster,
  findMonsterDef,
  type MonsterDef,
  type MonsterDexEntry,
} from './monsters';

const STYLE_ID = 'monsters-ui-style';

function ensureStyleInjected(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes monster-reveal-pop {
      0%   { transform: scale(0.5) rotate(-15deg); opacity: 0; }
      55%  { transform: scale(1.18) rotate(8deg); opacity: 1; }
      100% { transform: scale(1) rotate(0); opacity: 1; }
    }
    @keyframes monster-evolve-spin {
      0%   { transform: scale(0.5) rotate(0); opacity: 0; filter: brightness(2); }
      40%  { transform: scale(1.4) rotate(720deg); opacity: 1; filter: brightness(2); }
      75%  { transform: scale(1.1) rotate(1080deg); opacity: 1; filter: brightness(1.2); }
      100% { transform: scale(1) rotate(1080deg); opacity: 1; filter: brightness(1); }
    }
    .monster-reveal-banner { animation: monster-reveal-pop 0.55s ease-out; }
    .monster-evolve-overlay-icon { animation: monster-evolve-spin 1.6s cubic-bezier(.2,.7,.3,1) forwards; }
    @media (prefers-reduced-motion: reduce) {
      .monster-reveal-banner { animation: none !important; }
      .monster-evolve-overlay-icon { animation: none !important; }
    }
    .monster-dex-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
      gap: 10px;
      padding: 12px 0;
    }
    .monster-dex-cell {
      display: flex; flex-direction: column; align-items: center;
      gap: 4px;
      padding: 10px 6px;
      border-radius: 12px;
      background: #fafafa;
      border: 2px solid #e0e0e0;
      text-align: center;
      min-height: 110px;
    }
    .monster-dex-cell.caught {
      background: linear-gradient(135deg, #fff8e1 0%, #ffe082 100%);
      border-color: #ffb300;
    }
    .monster-dex-cell .sprite {
      font-size: 36px; line-height: 1;
    }
    .monster-dex-cell.locked .sprite {
      filter: grayscale(1) brightness(0.4);
      opacity: 0.5;
    }
    .monster-dex-cell .name {
      font-size: 12px; font-weight: 600; color: #333;
    }
    .monster-dex-cell.locked .name { color: #999; }
    .monster-dex-cell .count {
      font-size: 11px; color: #5d4037;
    }
    .monster-dex-cell .evolve-btn {
      font-size: 11px; padding: 3px 8px; border: none;
      background: #4caf50; color: white; border-radius: 6px;
      cursor: pointer; font-weight: 600;
    }
    .monster-dex-cell .evolve-btn:hover { background: #388e3c; }
    .monster-dex-tabs {
      display: flex; flex-wrap: wrap; gap: 6px;
      border-bottom: 2px solid #eee;
      padding-bottom: 8px;
    }
    .monster-dex-tab {
      padding: 6px 12px; border-radius: 8px 8px 0 0;
      background: #f5f5f5; border: none; cursor: pointer;
      font-size: 14px; font-weight: 600;
    }
    .monster-dex-tab.active {
      background: #1976d2; color: white;
    }
  `;
  document.head.appendChild(style);
}

// ── Home reveal banner ──────────────────────────────────────────────────────

/**
 * Render a one-shot "✨ 抓到新怪獸!" banner if `p.monsters.lastNew` is set.
 * Consumes the marker (clears + persists) so subsequent home renders don't
 * re-fire. Returns the unmount function (no-op when nothing was caught).
 *
 * Container is cleared before render — call with the same slot on every
 * home render (idempotent across renders).
 */
export function renderMonsterRevealBanner(
  container: HTMLElement,
  p: PlayerProgress,
): () => void {
  container.innerHTML = '';
  ensureStyleInjected();
  const def = consumeLastNew(p);
  if (!def) return () => { /* noop */ };

  const root = document.createElement('div');
  root.className = 'monster-reveal-banner';
  root.setAttribute('data-testid', 'monster-reveal-banner');
  root.setAttribute('data-monster-id', def.id);
  root.style.cssText = [
    'display:flex',
    'align-items:center',
    'gap:14px',
    'padding:14px 18px',
    'margin:8px 0',
    'border-radius:14px',
    'background:linear-gradient(135deg,#E1F5FE 0%,#81D4FA 100%)',
    'border:2px solid #0277BD',
    'box-shadow:0 3px 8px rgba(0,0,0,0.18)',
    'color:#01579B',
    'font-family:inherit',
    'cursor:pointer',
  ].join(';');

  const icon = document.createElement('div');
  icon.textContent = def.sprite;
  icon.style.cssText = 'font-size:42px;line-height:1;flex:0 0 auto;';

  const body = document.createElement('div');
  body.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:2px;';

  const headline = document.createElement('div');
  headline.style.cssText = 'font-size:17px;font-weight:700;line-height:1.2;';
  headline.textContent = `✨ 抓到新怪獸:${def.name}!`;

  const sub = document.createElement('div');
  sub.style.cssText = 'font-size:13px;line-height:1.3;color:#1565C0;';
  const factionName = FACTIONS[def.faction]?.displayName ?? def.faction;
  sub.textContent = `${factionName} · ${def.description}`;

  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:11px;color:#0277BD;margin-top:2px;';
  hint.textContent = '點我打開怪獸圖鑑 →';

  body.append(headline, sub, hint);
  root.append(icon, body);
  container.appendChild(root);

  // Click → open dex
  root.addEventListener('click', () => {
    openMonsterDexModal(p);
  });

  return () => { root.remove(); };
}

// ── Dex modal ───────────────────────────────────────────────────────────────

/** Faction tab order matches the spec faction list. */
const FACTION_ORDER: readonly FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'] as const;

/**
 * Open a full-screen Pokédex modal. Shows 5 faction tabs + grid of caught /
 * locked entries. Locked entries are shown silhouetted with ??? name.
 * Caught entries with >= EVOLVE_COST duplicates show an "進化!" button.
 *
 * Returns the close fn so callers can programmatically dismiss.
 */
export function openMonsterDexModal(p: PlayerProgress): () => void {
  ensureStyleInjected();
  if (typeof document === 'undefined') return () => { /* noop */ };

  // Dedupe — don't open twice
  const existing = document.querySelector('[data-testid="monster-dex-modal"]');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.setAttribute('data-testid', 'monster-dex-modal');
  overlay.style.cssText = [
    'position:fixed', 'inset:0',
    'background:rgba(0,0,0,0.7)',
    'z-index:200',
    'display:flex', 'align-items:center', 'justify-content:center',
    'padding:20px',
  ].join(';');

  const panel = document.createElement('div');
  panel.style.cssText = [
    'background:#fff',
    'border-radius:16px',
    'max-width:680px', 'width:100%',
    'max-height:90vh', 'overflow:auto',
    'padding:18px 22px',
    'font-family:inherit',
  ].join(';');

  const header = document.createElement('div');
  header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;';

  const snapshot = buildDexSnapshot(p);
  const title = document.createElement('h2');
  title.style.cssText = 'margin:0;font-size:20px;color:#333;';
  title.textContent = `📔 怪獸圖鑑 (${snapshot.distinctCaught} / ${snapshot.total})`;

  const closeBtn = document.createElement('button');
  closeBtn.textContent = '✕ 關閉';
  closeBtn.setAttribute('data-testid', 'monster-dex-close');
  closeBtn.style.cssText = [
    'background:#f44336', 'color:white', 'border:none',
    'padding:6px 14px', 'border-radius:8px',
    'cursor:pointer', 'font-size:14px', 'font-weight:600',
  ].join(';');

  header.append(title, closeBtn);
  panel.appendChild(header);

  // Faction tabs
  const tabs = document.createElement('div');
  tabs.className = 'monster-dex-tabs';
  const gridContainer = document.createElement('div');
  gridContainer.setAttribute('data-testid', 'monster-dex-grid-container');

  let activeFaction: FactionId = FACTION_ORDER[0];

  const renderGrid = (): void => {
    gridContainer.innerHTML = '';
    const grid = document.createElement('div');
    grid.className = 'monster-dex-grid';
    grid.setAttribute('data-testid', 'monster-dex-grid');
    grid.setAttribute('data-faction', activeFaction);

    const entries = snapshot.byFaction[activeFaction] ?? [];
    for (const entry of entries) {
      grid.appendChild(buildDexCell(p, entry, () => {
        // Refresh snapshot after evolve mutation
        const refreshed = buildDexSnapshot(p);
        snapshot.byFaction = refreshed.byFaction;
        snapshot.entries = refreshed.entries;
        snapshot.distinctCaught = refreshed.distinctCaught;
        title.textContent = `📔 怪獸圖鑑 (${snapshot.distinctCaught} / ${snapshot.total})`;
        renderGrid();
      }));
    }
    gridContainer.appendChild(grid);
  };

  for (const f of FACTION_ORDER) {
    const tab = document.createElement('button');
    tab.className = 'monster-dex-tab' + (f === activeFaction ? ' active' : '');
    tab.setAttribute('data-testid', `monster-dex-tab-${f}`);
    const fdef = FACTIONS[f];
    const grp = snapshot.byFaction[f] ?? [];
    const caughtN = grp.filter((e) => e.caught).length;
    tab.textContent = `${fdef.emoji} ${fdef.displayName} (${caughtN}/${grp.length})`;
    tab.addEventListener('click', () => {
      activeFaction = f;
      // Toggle active class
      tabs.querySelectorAll('.monster-dex-tab').forEach((el) => el.classList.remove('active'));
      tab.classList.add('active');
      renderGrid();
    });
    tabs.appendChild(tab);
  }
  panel.appendChild(tabs);
  panel.appendChild(gridContainer);
  renderGrid();

  // Footer hint
  const hint = document.createElement('p');
  hint.style.cssText = 'font-size:12px;color:#888;margin:12px 0 0;text-align:center;';
  hint.textContent = `集滿 ${EVOLVE_COST} 隻同款怪獸即可進化!答題、擊敗 boss、達成 streak / 徽章都有機會抓到。`;
  panel.appendChild(hint);

  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const close = (): void => { overlay.remove(); };
  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  return close;
}

/** Build one cell in the dex grid. `onChange` re-renders the grid on evolve. */
function buildDexCell(
  p: PlayerProgress,
  entry: MonsterDexEntry,
  onChange: () => void,
): HTMLElement {
  const cell = document.createElement('div');
  cell.className = 'monster-dex-cell' + (entry.caught ? ' caught' : ' locked');
  cell.setAttribute('data-testid', 'monster-dex-cell');
  cell.setAttribute('data-monster-id', entry.def.id);
  cell.setAttribute('data-caught', String(entry.caught));

  const sprite = document.createElement('div');
  sprite.className = 'sprite';
  sprite.textContent = entry.caught ? entry.def.sprite : '❓';
  cell.appendChild(sprite);

  const name = document.createElement('div');
  name.className = 'name';
  name.textContent = entry.caught ? `${entry.def.name}` : '???';
  cell.appendChild(name);

  const tierLabel = document.createElement('div');
  tierLabel.style.cssText = 'font-size:10px;color:#888;';
  tierLabel.textContent = `Tier ${entry.def.tier}`;
  cell.appendChild(tierLabel);

  if (entry.caught) {
    const count = document.createElement('div');
    count.className = 'count';
    count.textContent = `× ${entry.count}`;
    cell.appendChild(count);

    if (canEvolve(p, entry.def.id)) {
      const evoBtn = document.createElement('button');
      evoBtn.className = 'evolve-btn';
      evoBtn.setAttribute('data-testid', 'monster-evolve-btn');
      evoBtn.textContent = `🔥 進化!`;
      evoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const result = evolveMonster(p, entry.def.id);
        if (result) {
          showEvolveCelebration(result.from, result.to);
          onChange();
        }
      });
      cell.appendChild(evoBtn);
    }
  }

  return cell;
}

// ── Evolve celebration ──────────────────────────────────────────────────────

/**
 * Full-screen evolution celebration. Shows fromSprite → toSprite with a
 * spin animation. Auto-dismiss after ~2.5s; click anywhere to dismiss early.
 *
 * Designed to fire RIGHT AFTER `evolveMonster` returns successfully.
 * Returns the close fn for programmatic dismissal.
 */
export function showEvolveCelebration(from: MonsterDef, to: MonsterDef): () => void {
  ensureStyleInjected();
  if (typeof document === 'undefined') return () => { /* noop */ };

  // Dedupe — if a previous celebration is open, dismiss it
  const existing = document.querySelector('[data-testid="monster-evolve-overlay"]');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.setAttribute('data-testid', 'monster-evolve-overlay');
  overlay.setAttribute('data-from-id', from.id);
  overlay.setAttribute('data-to-id', to.id);
  overlay.style.cssText = [
    'position:fixed', 'inset:0',
    'background:radial-gradient(circle,rgba(255,236,179,0.95) 0%,rgba(255,143,0,0.85) 100%)',
    'z-index:300',
    'display:flex', 'flex-direction:column',
    'align-items:center', 'justify-content:center',
    'gap:18px',
    'color:#3E2723',
    'font-family:inherit',
    'cursor:pointer',
  ].join(';');

  const headline = document.createElement('div');
  headline.style.cssText = 'font-size:28px;font-weight:700;text-shadow:0 2px 6px rgba(255,255,255,0.6);';
  headline.textContent = '🌟 進化! 🌟';

  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;gap:24px;';

  const fromSprite = document.createElement('div');
  fromSprite.style.cssText = 'font-size:72px;opacity:0.55;filter:grayscale(0.4);';
  fromSprite.textContent = from.sprite;

  const arrow = document.createElement('div');
  arrow.style.cssText = 'font-size:48px;color:#BF360C;';
  arrow.textContent = '→';

  const toSprite = document.createElement('div');
  toSprite.className = 'monster-evolve-overlay-icon';
  toSprite.style.cssText = 'font-size:96px;';
  toSprite.textContent = to.sprite;

  row.append(fromSprite, arrow, toSprite);

  const caption = document.createElement('div');
  caption.style.cssText = 'font-size:20px;font-weight:600;text-align:center;';
  caption.textContent = `${from.name} → ${to.name}`;

  const hint = document.createElement('div');
  hint.style.cssText = 'font-size:13px;opacity:0.8;';
  hint.textContent = '點任何地方繼續';

  overlay.append(headline, row, caption, hint);
  document.body.appendChild(overlay);

  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    overlay.remove();
  };
  overlay.addEventListener('click', dismiss);
  setTimeout(dismiss, 2500);

  return dismiss;
}

/** Re-export catalogue + helpers for elsewhere (e.g. tests). */
export { findMonsterDef };
